import { cn, formatRank } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ActiveGame, ActiveGamePlayer } from "../types";
import { getChampionIconUrl, getChampionName, getRankImage, getRuneIconUrl, getRuneName, getSummonerSpellName, getSummonerSpellUrl } from "../utils/ddragon";
import { parseRiotId, summonerPath } from "../utils/regions";
import { mmss } from "../utils/time";

function formatGameMode(mode: string): string {
	const modeMap: Record<string, string> = {
		CLASSIC: "Summoner's Rift",
		ARAM: "ARAM",
		URF: "URF",
		ONEFORALL: "One for All",
		CHERRY: "Arena",
		TUTORIAL: "Tutorial"
	};
	return modeMap[mode] || mode;
}

function LiveBadge() {
	return (
		<span className="flex h-[26px] items-center gap-2 rounded-full bg-signal/[.12] px-2.5 text-[11px] font-extrabold tracking-[.1em] text-signal">
			<span className="relative h-[7px] w-[7px]" aria-hidden="true">
				<span className="absolute inset-0 rounded-full bg-signal animate-ping-soft" />
				<span className="absolute inset-0 rounded-full bg-signal" />
			</span>
			LIVE
		</span>
	);
}

function PlayerRow({ player, region }: { player: ActiveGamePlayer; region: string }) {
	const keystone = player.perks.perkIds[0];
	const id = parseRiotId(player.summonerName);
	const champName = getChampionName(player.championId);
	const r = player.rank;
	const total = r ? r.wins + r.losses : 0;

	return (
		<li
			aria-current={player.isTracked || undefined}
			className={cn(
				"flex items-center gap-2 rounded-lg p-1.5",
				player.isTracked ? "bg-signal/[.08] shadow-[inset_0_0_0_1px_rgb(198_244_50/.45)]" : "bg-raised"
			)}
		>
			<img src={getChampionIconUrl(player.championId)} alt={champName} className="h-10 w-10 shrink-0 rounded-lg" />
			<span className="flex shrink-0 flex-col gap-0.5">
				<img src={getSummonerSpellUrl(player.spell1Id)} alt={getSummonerSpellName(player.spell1Id)} className="block h-[18px] w-[18px] rounded" />
				<img src={getSummonerSpellUrl(player.spell2Id)} alt={getSummonerSpellName(player.spell2Id)} className="block h-[18px] w-[18px] rounded" />
			</span>
			{keystone ? <img src={getRuneIconUrl(keystone)} alt={getRuneName(keystone)} className="h-[22px] w-[22px] shrink-0 rounded-full bg-[#07090d]" /> : null}
			<span className="min-w-0 flex-1">
				{id ? (
					<Link to={summonerPath(region, id.name, id.tag)} className={cn("block truncate text-[13px] hover:underline", player.isTracked ? "font-extrabold" : "font-semibold")}>
						{id.name}
					</Link>
				) : (
					<span className={cn("block truncate text-[13px]", player.isTracked ? "font-extrabold" : "font-semibold")}>{player.summonerName}</span>
				)}
				<span className="block truncate text-[11px] text-ink-3">
					{champName}
					{player.summonerLevel != null && ` · Lv ${player.summonerLevel}`}
				</span>
			</span>
			<span className="flex shrink-0 items-center gap-1.5 text-right">
				<span>
					<span className="block text-xs font-bold">{r ? `${formatRank(r)}${r.tier !== "UNRANKED" && !/LP$/.test(formatRank(r)) ? ` · ${r.leaguePoints} LP` : ""}` : "Unranked"}</span>
					{r && total > 0 && (
						<span className="block text-[11px] text-ink-3">
							{Math.round((r.wins / total) * 100)}% · {total}g
						</span>
					)}
				</span>
				<img src={getRankImage(r?.tier ?? "UNRANKED")} alt="" className="h-7 w-7 object-contain" />
			</span>
		</li>
	);
}

function TeamColumn({ players, side, region }: { players: ActiveGamePlayer[]; side: "blue" | "red"; region: string }) {
	return (
		<div className="min-w-0 flex-[1_1_320px] border-line p-3 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-r md:[&:not(:last-child)]:border-b-0">
			<h3 className="mb-2 flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-[.08em] text-ink-2">
				<span aria-hidden="true" className={cn("h-2 w-2 rounded-[2px]", side === "blue" ? "bg-side-blue" : "bg-side-red")} />
				{side === "blue" ? "Blue side" : "Red side"}
			</h3>
			<ul className="m-0 flex list-none flex-col gap-1 p-0">
				{players.map(p => (
					<PlayerRow key={p.puuid} player={p} region={region} />
				))}
			</ul>
		</div>
	);
}

export function InGameCard({ game }: { game: ActiveGame }) {
	const { region = "" } = useParams<{ region: string }>();
	const elapsed = () => Math.max(0, Math.floor((Date.now() - game.gameStartTime) / 1000));
	const [seconds, setSeconds] = useState(elapsed);

	useEffect(() => {
		const interval = setInterval(() => setSeconds(elapsed()), 1000);
		return () => clearInterval(interval);
	}, [game.gameStartTime]); // eslint-disable-line react-hooks/exhaustive-deps

	const blue = game.participants.filter(p => p.teamId === 100);
	const red = game.participants.filter(p => p.teamId === 200);

	return (
		<div className="overflow-hidden rounded-xl border border-line-2 bg-surface">
			<div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-b border-line px-3.5 py-3">
				<LiveBadge />
				<span className="text-sm font-bold">{formatGameMode(game.gameMode)}</span>
				<span className="ml-auto font-mono text-[15px] font-semibold" aria-label="Game time" role="timer">
					{game.gameStartTime > 0 ? mmss(seconds) : "Loading in"}
				</span>
			</div>
			<div className="flex flex-wrap">
				<TeamColumn players={blue} side="blue" region={region} />
				<TeamColumn players={red} side="red" region={region} />
			</div>
			<div className="border-t border-line px-3.5 py-2.5 text-xs text-ink-3">Refreshes every 30 seconds while this tab is open.</div>
		</div>
	);
}
